import { Link } from "react-router-dom";
import { ArrowRight, Award, Building2, ShieldCheck, Trees, DollarSign } from "lucide-react";
import SectionHeader from "@/components/SectionHeader";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/0facd42c-679f-4c92-b16c-62255759199f/images/c302508b5d65f28ca2110b850f7eed1679f630db5fc6547f46db01058fc802df.png";
const HERO_PORTRAIT = "https://customer-assets.emergentagent.com/job_linda-burke/artifacts/l1o4htin_IMG_7945.jpeg";

const pillars = [
  { icon: Building2, label: "Infrastructure", desc: "Smart investments in roads, water, and public spaces." },
  { icon: ShieldCheck, label: "Public Safety", desc: "Funding our police, fire, and EMS teams." },
  { icon: Trees, label: "Parks & Community", desc: "Preserving green space and gathering places." },
  { icon: DollarSign, label: "Fiscal Stewardship", desc: "Balanced budgets. Honest planning. Real results." },
];

export default function Home() {
  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border" data-testid="hero-section">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ backgroundImage: `url(${HERO_BG})` }}
          aria-hidden
        />
        <div className="absolute inset-0 hero-overlay" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-36 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <div className="flag-rule"><span /><span /><span /></div>
              <span className="text-xs uppercase tracking-[0.24em] font-bold text-accent">
                Re-Elect Mayor
              </span>
            </div>
            <h1
              data-testid="hero-heading"
              className="font-serif font-black text-5xl md:text-6xl lg:text-7xl text-primary leading-[0.95] tracking-tight"
            >
              Linda <span className="text-accent">Burke</span>
              <span className="block text-2xl md:text-3xl font-normal italic text-foreground/80 mt-4 leading-snug">
                Proven Leadership. Strong Community. <br className="hidden md:block" />
                Bright Future.
              </span>
            </h1>
            <blockquote className="mt-8 border-l-4 border-accent pl-5 text-lg md:text-xl text-foreground/90 italic max-w-xl leading-relaxed">
              "Together, we've made South Lebanon stronger. Together, we'll keep moving forward."
            </blockquote>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/donate"
                data-testid="hero-donate-button"
                className="bg-accent text-accent-foreground px-7 py-3.5 font-bold uppercase tracking-wider hover:bg-[#8A1333] transition-colors inline-flex items-center gap-2"
              >
                Donate <ArrowRight size={18} />
              </Link>
              <Link
                to="/meet-linda"
                data-testid="hero-meet-linda-button"
                className="border-2 border-primary text-primary px-7 py-3 font-bold uppercase tracking-wider hover:bg-primary hover:text-primary-foreground transition-colors"
              >
                Meet Linda
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm">
              <Link to="/achievements" className="text-primary underline-offset-4 hover:underline font-medium">
                Accomplishments →
              </Link>
              <Link to="/priorities" className="text-primary underline-offset-4 hover:underline font-medium">
                Priorities →
              </Link>
              <Link to="/volunteer" className="text-primary underline-offset-4 hover:underline font-medium">
                Get Involved →
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5 hidden lg:block">
            <div className="relative">
              <div className="absolute -inset-3 border-2 border-accent" aria-hidden />
              <img
                src={HERO_PORTRAIT}
                alt="Mayor Linda Burke"
                className="relative w-full h-auto object-cover aspect-[4/5]"
                data-testid="hero-portrait"
              />
              <div className="absolute -bottom-6 -left-6 bg-primary text-primary-foreground px-6 py-4 max-w-[78%]">
                <div className="flag-rule mb-2"><span /><span /><span /></div>
                <div className="text-xs uppercase tracking-[0.22em] font-bold text-primary-foreground/80">Mayor of South Lebanon</div>
                <div className="font-serif text-xl font-bold mt-1 leading-tight">Linda Burke</div>
                <div className="text-xs text-primary-foreground/80 mt-1">Serving since January 2024</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="py-20 md:py-28 paper-grain">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionHeader
            eyebrow="What We Stand For"
            title="A record of results. A vision for what's next."
            intro="From repaved streets to expanded parks, every decision is rooted in what South Lebanon families need today — and what our children will inherit tomorrow."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pillars.map(({ icon: Icon, label, desc }) => (
              <div
                key={label}
                className="bg-card border border-border p-7 hover:border-primary transition-colors"
                data-testid={`pillar-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              >
                <div className="w-12 h-12 bg-primary text-primary-foreground flex items-center justify-center mb-5">
                  <Icon size={22} />
                </div>
                <h3 className="font-serif text-xl font-bold text-primary mb-2">{label}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-12">
            <Link
              to="/achievements"
              className="inline-flex items-center gap-2 text-primary font-bold uppercase tracking-wider text-sm border-b-2 border-accent pb-1 hover:text-accent transition-colors"
            >
              See the Full Record <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20 grid md:grid-cols-3 gap-10 items-center">
          <div className="md:col-span-2">
            <div className="flag-rule mb-4"><span /><span /><span /></div>
            <h3 className="font-serif text-3xl md:text-4xl font-bold tracking-tight">
              Join the movement to keep South Lebanon moving forward.
            </h3>
            <p className="mt-3 text-primary-foreground/80 text-lg">
              Volunteer, request a yard sign, or pledge your support.
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <Link
              to="/volunteer"
              className="bg-accent text-accent-foreground px-6 py-3.5 font-bold uppercase tracking-wider text-center hover:bg-[#8A1333] transition-colors"
              data-testid="cta-volunteer"
            >
              Get Involved
            </Link>
            <Link
              to="/donate"
              className="border-2 border-primary-foreground text-primary-foreground px-6 py-3 font-bold uppercase tracking-wider text-center hover:bg-primary-foreground hover:text-primary transition-colors"
              data-testid="cta-donate"
            >
              Pledge Support
            </Link>
          </div>
        </div>
      </section>

      {/* QUOTE */}
      <section className="py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Award className="mx-auto text-accent mb-6" size={36} />
          <p className="font-serif text-2xl md:text-3xl text-primary leading-relaxed italic">
            "South Lebanon is more than a place on the map. It's a community of neighbors, businesses, and families who deserve a mayor who shows up, listens, and delivers."
          </p>
          <div className="mt-6 text-sm uppercase tracking-[0.22em] text-muted-foreground font-bold">
            — Mayor Linda Burke
          </div>
        </div>
      </section>

      {/* COMMUNITY GALLERY */}
      <section className="py-20 md:py-28 bg-secondary/30 border-t border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionHeader
            eyebrow="In The Community"
            title="Showing up. Listening. Leading."
            intro="From Purple Heart dedications to groundbreakings to neighborhood gatherings — Mayor Burke believes leadership happens in person."
          />
          <div className="grid grid-cols-2 md:grid-cols-12 gap-3 md:gap-4" data-testid="community-gallery">
            <figure className="md:col-span-7 row-span-2 relative aspect-[4/3] md:aspect-auto overflow-hidden">
              <img src="https://customer-assets.emergentagent.com/job_linda-burke/artifacts/c3d6uz3f_IMG_7940.jpeg" alt="Mayor Burke at a community groundbreaking" className="absolute inset-0 w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              <figcaption className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-primary/95 to-transparent text-primary-foreground p-5">
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-primary-foreground/80">Groundbreaking</div>
                <div className="font-serif text-lg font-bold leading-tight mt-0.5">Breaking ground on long-overdue projects.</div>
              </figcaption>
            </figure>
            <figure className="md:col-span-5 relative aspect-[4/3] overflow-hidden">
              <img src="https://customer-assets.emergentagent.com/job_linda-burke/artifacts/chh5uv7b_IMG_7942.jpeg" alt="Purple Heart Village Dedication" className="absolute inset-0 w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              <figcaption className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-primary/95 to-transparent text-primary-foreground p-5">
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-primary-foreground/80">Honoring Veterans</div>
                <div className="font-serif text-lg font-bold leading-tight mt-0.5">Purple Heart Village dedication.</div>
              </figcaption>
            </figure>
            <figure className="md:col-span-5 relative aspect-[4/3] overflow-hidden">
              <img src="https://customer-assets.emergentagent.com/job_linda-burke/artifacts/yej446r4_IMG_7943.jpeg" alt="Mayor Burke at a civic event" className="absolute inset-0 w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              <figcaption className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-primary/95 to-transparent text-primary-foreground p-5">
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-primary-foreground/80">Civic Leadership</div>
                <div className="font-serif text-lg font-bold leading-tight mt-0.5">Representing South Lebanon.</div>
              </figcaption>
            </figure>
            <figure className="md:col-span-6 relative aspect-[4/3] overflow-hidden">
              <img src="https://customer-assets.emergentagent.com/job_linda-burke/artifacts/ndt7az6j_IMG_7944.jpeg" alt="Mayor Burke with supporters at Republican Women of Warren County" className="absolute inset-0 w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              <figcaption className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-primary/95 to-transparent text-primary-foreground p-5">
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-primary-foreground/80">Warren County</div>
                <div className="font-serif text-lg font-bold leading-tight mt-0.5">Republican Women of Warren County.</div>
              </figcaption>
            </figure>
            <figure className="md:col-span-6 relative aspect-[4/3] overflow-hidden">
              <img src="https://customer-assets.emergentagent.com/job_linda-burke/artifacts/8o3wcswg_IMG_7941.jpeg" alt="Mayor Burke at work in her office" className="absolute inset-0 w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              <figcaption className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-primary/95 to-transparent text-primary-foreground p-5">
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-primary-foreground/80">Door Always Open</div>
                <div className="font-serif text-lg font-bold leading-tight mt-0.5">Listening to residents, every day.</div>
              </figcaption>
            </figure>
          </div>
        </div>
      </section>
    </div>
  );
}
